import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, ShoppingBag, Search } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import { useCart } from "@/pages/Shop";

const links = [
  { to: "/", label: "Home" },
  { to: "/events", label: "Events" },
  { to: "/results", label: "Results" },
  { to: "/blog", label: "Blog" },
  { to: "/gallery", label: "Gallery" },
  { to: "/shop", label: "Shop" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { settings } = useSettings();
  const { items } = useCart();
  const location = useLocation();
  const cartCount = items.reduce((s, i) => s + i.qty, 0);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="public-navbar"
      className={`sticky top-0 z-30 transition-all ${scrolled ? "bg-[var(--pz-ink)]/95 backdrop-blur border-b border-[var(--pz-line)]" : "bg-transparent"}`}
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link to="/" data-testid="brand-link" className="flex items-center gap-3 group">
           <img src="/logo.png" alt="Logo" className="h-10 w-auto object-contain" />
           <div className="leading-none">
               <div className="font-display text-xl font-bold uppercase tracking-wide text-white">{settings?.site_name || "PlayZone"}</div>
               <div className="text-[10px] uppercase tracking-[0.25em] text-[var(--pz-mute)] mt-1">Team Club</div>
           </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `relative text-sm uppercase tracking-[0.12em] font-bold transition ${
                  isActive ? "text-white" : "text-zinc-300 hover:text-white"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  <span className={`absolute -bottom-2 left-0 right-0 h-[3px] bg-[var(--pz-red)] transition-all ${isActive ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`} />
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Link to="/shop" data-testid="nav-cart-link" className="hidden sm:grid relative h-10 w-10 place-items-center rounded-full border border-[var(--pz-line)] text-white hover:border-[var(--pz-red)] transition">
            <ShoppingBag className="h-4 w-4" />
            {cartCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-[var(--pz-red)] text-white text-[10px] h-5 min-w-[20px] px-1 rounded-full grid place-items-center font-bold">{cartCount}</span>
            )}
          </Link>
          <Link to="/contact" data-testid="nav-book-now" className="hidden lg:inline-flex btn-red">Book Now</Link>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((o) => !o)}
            className="lg:hidden h-10 w-10 grid place-items-center rounded-full border border-[var(--pz-line)] text-white"
            aria-label="Menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[var(--pz-line)] bg-[var(--pz-ink)]">
          <div className="px-4 py-3 flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`mnav-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-3 py-2.5 rounded-lg uppercase tracking-[0.12em] text-sm font-bold border-l-2 ${isActive ? "border-[var(--pz-red)] text-white bg-[var(--pz-ink-2)]" : "border-transparent text-zinc-300"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link to="/contact" data-testid="mnav-book-now" className="btn-red mt-2 w-fit">Book Now</Link>
          </div>
        </div>
      )}
    </header>
  );
}
